import { toast } from "react-toastify";
import { store } from "../../../redux/oldStore";
import { ServerResponse } from "../../../utils/Server";
import { INotificationData } from "../MobileAppTypes";

/**
 * NotificationController - Centralized API handler for notification operations
 * Handles all notification-related API calls with consistent error handling
 */
export class NotificationController {
  /**
   * Fetch all notifications for a specific app
   * @param appId - The app ID to fetch notifications for
   * @returns Promise with list of notifications or error
   */
  static async listNotifications(appId: string): Promise<ServerResponse<INotificationData[]>> {
    try {
      const response = await store.server.getApi2<INotificationData[]>(`/MobileApp/ListNotifications/${appId}`);
      
      if (!response.Success) {
        console.error('Failed to fetch notifications:', response.Message);
        toast.error(response.Message || 'Failed to fetch notifications');
      }
      
      return response;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Error fetching notifications');
      return {
        Success: false,
        LoginRequired: false,
        Message: 'Error fetching notifications',
        GoTo: '',
        Value: []
      };
    }
  }

  /**
   * Cancel a scheduled notification
   * @param notificationId - The ID of the notification to cancel
   * @returns Promise with success status
   */
  static async cancelNotification(notificationId: string): Promise<ServerResponse<boolean>> {
    try {
      const response = await store.server.getApi2<boolean>(`/MobileApp/CancelNotification/${notificationId}`);
      
      if (response.Success) {
        toast.success('Notification cancelled successfully');
      } else {
        console.error('Failed to cancel notification:', response.Message);
        toast.error(response.Message || 'Failed to cancel notification');
      }
      
      return response;
    } catch (error) {
      console.error('Error cancelling notification:', error);
      toast.error('Error cancelling notification');
      return {
        Success: false,
        LoginRequired: false,
        Message: 'Error cancelling notification',
        GoTo: '',
        Value: false
      };
    }
  }

  /**
   * Schedule or send a notification immediately
   * @param notificationData - The notification data to send
   * @returns Promise with created notification data
   */
  static async scheduleNotification(appId:string, notificationData: INotificationData): Promise<ServerResponse<INotificationData>> {
    try {
      const response = await store.server.postApiWithServerResponse<INotificationData>(`/MobileApp/ScheduleNotification/${appId}`, notificationData);
      
      if (response.Success) {
        const isScheduled = notificationData.Scheduled && notificationData.ScheduledForUtc;
        const message = isScheduled ? 'Notification scheduled successfully' : 'Notification sent successfully';
        toast.success(message);
      } else {
        console.error('Failed to schedule notification:', response.Message);
        toast.error(response.Message || 'Failed to schedule notification');
      }
      
      return response;
    } catch (error) {
      console.error('Error scheduling notification:', error);
      toast.error('Error scheduling notification');
      return {
        Success: false,
        LoginRequired: false,
        Message: 'Error scheduling notification',
        GoTo: '',
        Value: {} as INotificationData
      };
    }
  }

  /**
   * Helper method to check if a notification can be cancelled
   * @param notification - The notification to check
   * @returns boolean indicating if the notification can be cancelled
   */
  static canCancelNotification(notification: INotificationData): boolean {
    // Can only cancel notifications that haven't been sent yet
    return !notification.Sent && (notification.Scheduled || false) && !notification.CanceledAtUtc;
  }

  /**
   * Helper method to get notification status display text
   * @param notification - The notification to get status for
   * @returns string representing the notification status
   */
  static getNotificationStatus(notification: INotificationData): string {
    if (notification.Sent) {
      return 'Sent';
    }
    if (notification.Scheduled && notification.ScheduledForUtc) {
      const scheduledDate = new Date(notification.ScheduledForUtc);
      const now = new Date();
      if (scheduledDate > now) {
        return 'Scheduled';
      } else {
        return 'Pending';
      }
    }
    return 'Draft';
  }

  /**
   * Helper method to parse notification data safely
   * @param data - The data object to parse
   * @returns parsed data with fallback values
   */
  static parseNotificationData(data?: Record<string, any>): { title: string; body: string; priority: string } {
    const defaults = {
      title: 'Untitled Notification',
      body: 'No content',
      priority: 'normal'
    };

    if (!data) return defaults;

    return {
      title: data.title || defaults.title,
      body: data.body || defaults.body,
      priority: data.priority || defaults.priority
    };
  }
}